import _objectSpread from '@babel/runtime/helpers/objectSpread2';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import { useSensors, useSensor, PointerSensor, KeyboardSensor, DndContext, rectIntersection, DragOverlay } from '@dnd-kit/core';
import { sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import * as React from 'react';
import isEqual from 'react-fast-compare';
import { useDebouncedCallback } from 'use-debounce';
import { useDndAnnouncements } from './use_dnd_announcements.js';
import { useDndEvents } from './use_dnd_events.js';
import { convertToDndKitDropAnimation } from './utils.js';
import { jsxs, jsx } from 'react/jsx-runtime';

var Context = /*#__PURE__*/React.createContext(null);
var DndContextV2 = function DndContextV2(props) {
  var activationConstraint = props.activationConstraint,
    announcements = props.announcements,
    children = props.children,
    collisionDetection = props.collisionDetection,
    _props$dropAnimation = props.dropAnimation,
    dropAnimation = _props$dropAnimation === void 0 ? "default" : _props$dropAnimation,
    onDragEnd = props.onDragEnd,
    onDragOver = props.onDragOver,
    onDragStart = props.onDragStart;
  var _React$useState = React.useState(null),
    _React$useState2 = _slicedToArray(_React$useState, 2),
    activeDragRender = _React$useState2[0],
    setActiveDragRender = _React$useState2[1];
  var droppableInfoPerDroppableIdRef = React.useRef({});
  var itemsPerDroppableIdRef = React.useRef({});
  var _React$useState3 = React.useState({}),
    _React$useState4 = _slicedToArray(_React$useState3, 2),
    itemsPerDroppableId = _React$useState4[0],
    setItemsPerDroppableId = _React$useState4[1];
  var itemsPerDroppableIdOnDragStartRef = React.useRef({});
  var setItemsPerDroppableIdDebounce = useDebouncedCallback(function (val) {
    return setItemsPerDroppableId(val);
  }, 100);
  var dndAnnouncements = useDndAnnouncements(_objectSpread({
    itemsPerDroppableId: itemsPerDroppableId,
    itemsPerDroppableIdOnDragStartRef: itemsPerDroppableIdOnDragStartRef
  }, announcements || {}));
  var _useDndEvents = useDndEvents({
      droppableInfoPerDroppableIdRef: droppableInfoPerDroppableIdRef,
      itemsPerDroppableId: itemsPerDroppableId,
      itemsPerDroppableIdOnDragStartRef: itemsPerDroppableIdOnDragStartRef,
      onDragEnd: onDragEnd,
      onDragOver: onDragOver,
      onDragStart: onDragStart,
      setItemsPerDroppableId: setItemsPerDroppableId,
      setActiveDragRender: setActiveDragRender
    }),
    handleDragCancel = _useDndEvents.handleDragCancel,
    handleDragEnd = _useDndEvents.handleDragEnd,
    handleDragOver = _useDndEvents.handleDragOver,
    handleDragStart = _useDndEvents.handleDragStart;
  var sensors = useSensors(useSensor(PointerSensor, {
    activationConstraint: activationConstraint
  }), useSensor(KeyboardSensor, {
    coordinateGetter: sortableKeyboardCoordinates
  }));
  var context = React.useMemo(function () {
    return {
      itemsPerDroppableId: itemsPerDroppableId,
      setDroppableInfoPerDroppableIdRef: function setDroppableInfoPerDroppableIdRef(droppableId, info) {
        droppableInfoPerDroppableIdRef.current[droppableId] = info;
      },
      setItemsPerDroppableId: function setItemsPerDroppableId(droppableId, items) {
        itemsPerDroppableIdRef.current[droppableId] = items;
        if (!isEqual(itemsPerDroppableIdRef.current, itemsPerDroppableId)) {
          setItemsPerDroppableIdDebounce(itemsPerDroppableIdRef.current);
        }
      }
    };
  }, [itemsPerDroppableId, setItemsPerDroppableIdDebounce]);
  return /*#__PURE__*/jsxs(DndContext, {
    accessibility: {
      announcements: dndAnnouncements
    },
    collisionDetection: collisionDetection || rectIntersection,
    onDragStart: handleDragStart,
    onDragOver: handleDragOver,
    onDragEnd: handleDragEnd,
    onDragCancel: handleDragCancel,
    sensors: sensors,
    children: [/*#__PURE__*/jsx(Context.Provider, {
      value: context,
      children: children
    }), /*#__PURE__*/jsx(DragOverlay, {
      dropAnimation: convertToDndKitDropAnimation(dropAnimation),
      children: activeDragRender ? activeDragRender : null
    })]
  });
};
DndContextV2.displayName = "DndContextV2";
function useDndContext() {
  return React.useContext(Context);
}

export { DndContextV2, useDndContext };
